import React, {
  useRef, useState, useEffect, useCallback,
} from 'react';
import classNames from 'classnames';
import './SelectInputStyles.scss';
import { useDispatch } from 'react-redux';
import { Icons } from '../../base/BootstrapComponents/BootstrapComponents';
import Dropdown from '../../base/Dropdown/Dropdown';
import TextInput from '../TextInput/TextInput';
import renderMessage from '../renderMessage';
import { errorNotification } from '../../../modules/Notifications/notificationDecorator';
import { getIsRequired, getResponseError } from '../../../utils/utils';
import { metaDataFormatter, renderRegistryOptionMin } from '../../../utils/formatters';
import Loader from '../../base/Loader/Loader';
import { LOADING_DELAY } from '../../../configs';

export default function CustomRegistryNumberSelector(props) {
  const dispatch = useDispatch();
  const {
    inputProps = null,
    className = '',
    fieldState = {
      error: '',
      touched: false,
    },
    variant = 'default',
    label,
    disabled = false,
    dtoObject,
    customValidator = [],
    onSelectNumber = () => {},
    loadAction,
    formMeta,
    customMetaName = '',
  } = props;
  const metaName = customMetaName || props?.name || '';
  const { value = '', onChange = () => {}, name = '' } = inputProps;
  const { error } = fieldState;
  const isError = Boolean(error);
  const isRequired = getIsRequired(dtoObject, customValidator);
  const [loader, setLoader] = useState(false);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const anchorElement = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);
  const [options, setOptions] = useState([]);
  const [timer, setTimer] = useState(null);
  let currentOption = options.find(option => option.value === inputValue);
  if (inputValue && !currentOption) {
    currentOption = { number: inputValue, value: inputValue };
  }

  const handleClickOutside = useCallback(event => {
    if (!edit) {
      return;
    }
    const isClickByInput = anchorElement?.current && anchorElement.current.contains(event.target);
    const isClickByMenu = menuRef?.current && menuRef.current.contains(event.target);
    if (isClickByInput || isClickByMenu) {
      return;
    }
    if (onChange && value !== inputValue) {
      loadOptions(inputValue, true);
      onChange({ target: { value: inputValue, name } });
    }
    setEdit(false);
    setOpen(false);
  }, [edit, inputValue]);
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    if (!loadAction || !value) return;
    loadOptions(inputValue);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const optionFormatter = item => ({
    value: item.number, name: item.number, applicant: item, number: item.number,
  });

  const handleFocus = () => {
    setOpen(true);
    setEdit(true);
  };

  const onChangeInput = event => {
    setInputValue(event.target.value);
    onChange({ target: { value: event.target.value, name } });
    if (timer) {
      clearTimeout(timer);
    }
    setTimer(setTimeout(() => { loadOptions(event.target.value, true); }, LOADING_DELAY));
  };

  const loadOptions = (value, isSelect) => {
    if (!loadAction) return;
    setLoader(true);
    dispatch(loadAction(value))
      .then(data => {
        setLoader(false);
        if (!data?.length) { return; }
        const opt = data.map(optionFormatter);
        setOptions(opt);
        if (isSelect) {
          const co = opt.find(option => option.value === value);
          onSelectNumber(co ? [co] : []);
        }
      })
      .catch(response => {
        setLoader(false);
        debugger;
        errorNotification('Загрузка данных', getResponseError(response));
      });
  };

  const setInputFocus = () => {
    setEdit(true);
    if (!inputRef || !inputRef.current) return;
    inputRef.current.focus();
  };

  const handleClose = () => {
    setOpen(false);
    setEdit(false);
  };

  const toggleOpen = open => {
    if (disabled) return;
    setOpen(!open);
  };

  const onSelect = item => {
    if (!onChange) return;
    setEdit(false);
    const v = item.value;
    setInputValue(v);
    onChange({ target: { value: v, name } });
    onSelectNumber(v ? [item] : []);
    handleClose();
  };

  const onClear = () => {
    if (!onChange) return;
    setInputValue('');
    onChange({ target: { value: '', name } });
    onSelectNumber([]);
  };

  return (
    <div className={classNames('form-control-wrapper custom-registry-selector', className)}>
      <Dropdown
        dropBottom
        usePortal
        customControl
        customOpen={options?.length && open}
        onToggleOpen={toggleOpen}
        customHandleClose={handleClose}
        disabled={disabled}
      >
        <Dropdown.Toggle className="select-toggle">
          <div
            ref={anchorElement}
            className={classNames('form-control-wrapper multiple-select', className, { material: variant === 'material' }, { default: variant !== 'material' })}
            onClick={setInputFocus}
          >
            <div className={classNames('input-block', { 'is-invalid': isError })}>
              {metaDataFormatter(formMeta, metaName, renderMessage(label))}
              <div className="multiple-value-block">
                {!edit && currentOption ? (
                  <div className="multiple-value registry-value">
                    {renderRegistryOptionMin(currentOption)}
                    <Icons
                      customIcon
                      className="multiple-value-delete"
                      width={24}
                      height={24}
                      icon="close"
                      onClick={event => {
                        onClear(currentOption);
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                    />
                  </div>
                ) : null}
                <TextInput
                  className={classNames({ 'no-edit': !edit })}
                  inputProps={{
                    ref: inputRef,
                    onChange: onChangeInput,
                    value: inputValue,
                    onFocus: handleFocus,
                    // onBlur: handleBlur,
                  }}
                />
              </div>
              <div htmlFor={props.id} className="form-input-label">
                {renderMessage(label)}{isRequired ? <span className="form-label-mark-required">*</span> : null}
              </div>
              <span className="input-after-block">
                <div className="select-arrow-wrapper">
                  <Loader loading={loader} />
                  <Icons customIcon className="select-arrow" width={12} height={8} icon="arrow-down" />
                </div>
              </span>
            </div>
            {isError ? <div className="invalid-feedback">{renderMessage(error)}</div> : null}
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu
          key={options}
          disableClickToggle={false}
          rightPadding={32}
          className="select-options-wrapper"
          anchorElement={anchorElement}
          classNamePortal="portal-select-options multiple-select-option"
          useResizeContent={false}
        >
          {options?.length ? (
            <div className="select-options-list" ref={menuRef}>
              {options.map((item, key) => {
                const isSelected = item.value === value;
                return (
                  <div
                    key={key}
                    className={classNames('select-option', { selected: isSelected })}
                    onClick={event => {
                      onSelect(item);
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                  >
                    {renderRegistryOptionMin(item)}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="select-options-empty-list">
              Список пуст
            </div>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}
